// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, Element, h, Prop } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'
import type { Size } from '../shared'

/**
 * `<qds-label>` elements represent a caption for an item in a user interface.
 *
 * @see https://quartz.se.com/build/components/label
 */
@Component({
  tag: 'qds-label',
  shadow: true,
  styleUrl: 'label.css',
})
export class Label implements ComponentInterface {
  /**
   * Adds vertical margin to the label for alignment.
   *
   * This is useful when creating inline layouts so that the first lines have
   * the correct vertical centering.
   */
  @Prop() public readonly inline: boolean = false

  /**
   * Specify the labelled item as required by appending a red asterisk (*).
   */
  @Prop() public readonly required: boolean = false

  /**
   * The label's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The label's text.
   */
  @Prop() public readonly text?: string

  @Element() private readonly host!: HTMLQdsLabelElement

  #inheritedAttributes: Attributes = {}

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
  }

  public render() {
    return (
      <span
        class={{
          'qds-inline': this.inline,
          'qds-label': true,
        }}
        data-size={this.#computedSize}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.#inheritedAttributes}
      >
        {this.required && <span class="qds-required">*</span>}
        {this.text}
      </span>
    )
  }
}
